<div [class]="layout.shell">
  <header [class]="layout.header">
    <div class="flex items-center gap-2">
      <mat-icon class="h-5! w-5! shrink-0 text-xl! leading-5! text-primary-600">{{ isCreate ? "add_circle" : "edit_note" }}</mat-icon>
      <h2
        id="prescription-form-title"
        class="min-w-0 flex-1 truncate text-lg! leading-5! font-semibold! text-neutral-800"
      >
        {{ (isCreate ? "guaranteeV2.action.addPrescription" : "guaranteeV2.action.editLine") | translate }}
      </h2>
    </div>
  </header>

  <div [class]="layout.body">
    <!-- Kind first: it settles which dictionary names the service and whether a course is stated. -->
    <div class="flex min-w-0 flex-col gap-1">
      <span guaranteesLabel>{{ "guaranteeV2.label.prescription" | translate }}</span>
      <!-- Not clearable: a line is always of one kind. The placeholder shows only for stored lines whose kind is not authorable here. -->
      <guarantees-option-picker
        [options]="kindOptions"
        [clearable]="false"
        [placeholder]="'guaranteeV2.label.kindUnknown' | translate"
        [value]="kind()"
        (valueChange)="setKind($event)"
      ></guarantees-option-picker>
    </div>

    <section class="flex min-w-0 flex-col gap-3">
      <!-- Stated, never inferred from an empty code: the engine reads it as a deliberate act. -->
      <button
        guaranteesToggle
        [pressed]="isFreeForm()"
        shape="card"
        layout="block"
        size="lg"
        type="button"
        [disabled]="freeFormLocked()"
        (click)="toggleFreeForm()"
      >
        <guarantees-check-box [checked]="isFreeForm()"></guarantees-check-box>
        <span class="flex min-w-0 flex-col">
          <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.freeForm" | translate }}</span>
          <!-- Locked on «Інше», where the hint is the reason rather than the invitation. -->
          <span class="text-sm text-neutral-400">{{ (freeFormLocked() ? "guaranteeV2.message.freeFormOnlyHint" : "guaranteeV2.message.freeFormHint") | translate }}</span>
        </span>
      </button>

      <!-- The picker serves both modes: free-form only adds the right to amend what it filled in. -->
      <label class="flex flex-col gap-1">
        <span
          guaranteesLabel
          [required]="isCoded()"
          [missing]="isCoded() && serviceMissing()"
          >{{ "guaranteeV2.label.serviceCode" | translate }}</span
        >
        <guarantees-service-picker
          [kind]="kind()"
          [initial]="{ code: code(), name: name() }"
          (selected)="pickService($event)"
        ></guarantees-service-picker>
        <!-- Said where the answer is owed, not only under the greyed-out button at the bottom. -->
        <span [class]="fieldMessage">
          @if (isCoded() && serviceMissing()) {
            {{ "guaranteeV2.message.prescriptionNeedsService" | translate }}
          }
        </span>
      </label>

      <!-- Said before saving rather than after ordering, which is where it would otherwise surface. -->
      @if (showsUnmapped()) {
        <p class="flex items-start gap-1.5 rounded-lg border border-warning-200 bg-warning-50/60 px-2 py-1.5 text-sm text-warning-800">
          <mat-icon class="mt-px h-4! w-4! shrink-0 text-base! leading-4!">link_off</mat-icon>
          {{ "guaranteeV2.message.unmappedFormHint" | translate }}
        </p>
      }

      <!-- Editable only free-form: a coded line is named by the dictionary, and the two must agree. -->
      @if (!isCoded()) {
        <div class="flex min-w-0 flex-col gap-3 sm:flex-row">
          <label class="flex min-w-0 flex-1 flex-col gap-1">
            <span
              guaranteesLabel
              required
              [missing]="serviceMissing()"
              >{{ "guaranteeV2.label.prescription" | translate }}</span
            >
            <input
              guaranteesControl
              class="w-full"
              name="name"
              type="text"
              autocomplete="off"
              [placeholder]="'guaranteeV2.message.prescriptionNamePlaceholder' | translate"
              [ngModel]="name()"
              (ngModelChange)="editName($event)"
            />
            <span [class]="fieldMessage">
              @if (serviceMissing()) {
                {{ "guaranteeV2.message.prescriptionNeedsName" | translate }}
              }
            </span>
          </label>

          <label class="flex flex-col gap-1 sm:w-32">
            <span guaranteesLabel>{{ "guaranteeV2.label.serviceCode" | translate }}</span>
            <input
              guaranteesControl
              class="w-full font-mono"
              name="code"
              type="text"
              autocomplete="off"
              [ngModel]="code()"
              (ngModelChange)="editCode($event)"
            />
          </label>
        </div>
      }

      <!-- For a medication the period comes later, after the drug itself is settled. -->
      @if (!isMedication()) {
        <div class="flex min-w-0 flex-col gap-3 sm:flex-row">
          <!-- How many rows the line becomes: two massages are two lines, each delivered on its own. -->
          @if (asksQuantity()) {
            <label class="flex flex-col gap-1 sm:w-24">
              <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.quantity" | translate }}</span>
              <input
                guaranteesControl
                class="w-full tabular-nums"
                name="quantity"
                type="number"
                min="1"
                step="1"
                [disabled]="isSingleUnitKind()"
                [matTooltip]="isSingleUnitKind() ? ('guaranteeV2.message.singleUnitKindHint' | translate) : ''"
                [ngModel]="quantity()"
                (ngModelChange)="quantity.set($event)"
              />
            </label>
          }

          <!-- When the line may be delivered: what a booking is checked against, and what expires it. -->
          <label class="flex min-w-0 flex-1 flex-col gap-1">
            <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.prescribedPeriod" | translate }}</span>
            <span class="flex items-center gap-1.5">
              <input
                guaranteesControl
                class="min-w-0 flex-1 tabular-nums"
                name="startDate"
                type="date"
                [ngModel]="startDate()"
                (ngModelChange)="startDate.set($event)"
              />
              <span class="shrink-0 text-sm text-neutral-400">&ndash;</span>
              <input
                guaranteesControl
                class="min-w-0 flex-1 tabular-nums"
                name="endDate"
                type="date"
                [ngModel]="endDate()"
                (ngModelChange)="endDate.set($event)"
              />
            </span>
          </label>
        </div>
      }

      <!-- Optional, and only for a visit: most lines are delivered wherever the member finds a slot,
           and only some are sent to a particular clinic or a doctor already treating them. -->
      @if (restrictable()) {
        <div class="flex flex-col gap-1.5">
          <span class="flex flex-col">
            <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.fulfillmentPlace" | translate }}</span>
            <span class="text-sm text-neutral-400">{{ "guaranteeV2.message.fulfillmentPlaceHint" | translate }}</span>
          </span>

          <guarantees-provider-restriction
            [service]="restrictionService()"
            [initial]="restriction()"
            (changed)="restriction.set($event)"
          ></guarantees-provider-restriction>
        </div>
      }
    </section>

    <!-- Medication only: everything else is delivered once. -->
    @if (isMedication()) {
      <section class="flex min-w-0 flex-col gap-3">
        <!-- What is dispensed, before how much and for how long: stated in that order below. -->
        <div class="flex min-w-0 flex-col gap-3 sm:flex-row">
          <label class="flex min-w-0 flex-1 flex-col gap-1">
            <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.form" | translate }}</span>
            <!-- Coded: chosen, not typed — a form the dictionary lacks is an order that matches nothing.
               Free-form: the line is the manager's own words, the form included. -->
            @if (isFreeForm()) {
              <input
                guaranteesControl
                class="w-full"
                name="form"
                type="text"
                autocomplete="off"
                [ngModel]="form()"
                (ngModelChange)="form.set($event)"
              />
            } @else {
              <guarantees-option-picker
                [options]="formPickerOptions()"
                [disabled]="formOptions().length === 0"
                [placeholder]="(formOptions().length ? 'guaranteeV2.label.anyForm' : 'guaranteeV2.message.formNeedsDrug') | translate"
                [value]="form()"
                (valueChange)="form.set($event)"
              ></guarantees-option-picker>
            }
          </label>

          <!-- Stated by the dictionary, never asked: what the packages figure below is judged by. -->
          @if (packageSizes().length > 0) {
            <div class="flex shrink-0 flex-col gap-1 sm:w-36">
              <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.quantityInPackage" | translate }}</span>
              <span class="flex min-h-7 items-center text-sm text-neutral-600 tabular-nums">
                @for (size of packageSizes(); track size; let last = $last) {
                  {{ size | number: "1.0-2" }}
                  @if (!last) {
                    ,&nbsp;
                  }
                }
              </span>
            </div>
          }
        </div>

        <!-- What the pick may be dispensed as, narrowed live by the chosen form. -->
        @if (soldDrugs().length > 0) {
          <section class="flex min-w-0 flex-col rounded-lg border border-neutral-200">
            <button
              class="flex items-center gap-1.5 px-2.5 py-2 text-sm font-semibold text-neutral-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
              type="button"
              (click)="trademarksExpanded.set(!trademarksExpanded())"
            >
              <mat-icon class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400">{{ trademarksExpanded() ? "expand_less" : "expand_more" }}</mat-icon>
              {{ "guaranteeV2.label.trademarks" | translate }}
              <span class="rounded-full bg-neutral-100 px-1.5 font-normal text-neutral-500 tabular-nums">{{ soldDrugs().length }}</span>
            </button>

            @if (trademarksExpanded()) {
              <ul class="flex flex-col gap-1 border-t border-neutral-100 px-2.5 py-2">
                @for (drug of soldDrugs(); track $index) {
                  <!-- Centred, not baselined: the package badge is taller than the text beside it. -->
                  <li class="flex min-w-0 items-center gap-2 text-sm">
                    <span class="min-w-0 flex-1 truncate text-neutral-700">{{ drug.trademarkFullName || drug.nameUa }}</span>
                    @if (drug.formShort) {
                      <span class="shrink-0 text-neutral-400">{{ drug.formShort }}</span>
                    }
                    @if (drug.quantityInPackage) {
                      <span
                        class="shrink-0 text-neutral-400 tabular-nums"
                        [matTooltip]="'guaranteeV2.label.quantityInPackage' | translate"
                        >&times;{{ drug.quantityInPackage | number: "1.0-2" }}</span
                      >
                    }

                    <!-- What the stated units come to in THIS product's boxes: the same course is a
                       different number of packages in each of them. -->
                    @if (packagesFor(drug); as packages) {
                      <span
                        class="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-info-50 px-1.5 font-semibold text-info-700 tabular-nums"
                        [matTooltip]="'guaranteeV2.message.packagesForItemHint' | translate: { count: packages }"
                      >
                        <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">inventory_2</mat-icon>
                        {{ packages }}
                      </span>
                    }
                  </li>
                }
              </ul>
            }
          </section>
        }

        <!--
        How much the course comes to, stated one way or the other: the units prescribed, or the
        packages they are ordered in. Whichever is answered closes the other, so the line never
        states an amount the pharmacy order would not be placed for.
      -->
        <div class="flex min-w-0 flex-col gap-3 sm:flex-row">
          <label class="flex min-w-0 flex-1 flex-col gap-1">
            <span
              class="text-sm font-semibold"
              [class]="governingAmount() === 'packages' ? 'text-neutral-400' : 'text-neutral-700'"
              >{{ "guaranteeV2.label.requiredQuantity" | translate }}</span
            >
            <input
              guaranteesControl
              class="w-full tabular-nums"
              name="requiredQuantity"
              type="number"
              min="0"
              [disabled]="governingAmount() === 'packages'"
              [ngModel]="requiredQuantity()"
              (ngModelChange)="setRequiredQuantity($event)"
            />
          </label>

          @let packagesClosed = governingAmount() === "quantity";

          <label class="flex min-w-0 flex-1 flex-col gap-1">
            <span
              class="text-sm font-semibold"
              [class]="packagesClosed ? 'text-neutral-400' : 'text-neutral-700'"
              >{{ "guaranteeV2.label.treatmentPackages" | translate }}</span
            >
            <input
              guaranteesControl
              class="w-full tabular-nums"
              name="treatmentPackages"
              type="number"
              min="0"
              [disabled]="packagesClosed"
              [ngModel]="treatmentPackages()"
              (ngModelChange)="treatmentPackages.set($event)"
            />
          </label>
        </div>

        <p class="text-sm text-neutral-400">{{ "guaranteeV2.message.packagesPrecedenceHint" | translate }}</p>

        <!-- When the line may be delivered: what a booking is checked against, and what expires it. -->
        <label class="flex min-w-0 flex-col gap-1">
          <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.prescribedPeriod" | translate }}</span>
          <span class="flex items-center gap-1.5">
            <input
              guaranteesControl
              class="min-w-0 flex-1 tabular-nums"
              name="startDate"
              type="date"
              [ngModel]="startDate()"
              (ngModelChange)="startDate.set($event)"
            />
            <span class="shrink-0 text-sm text-neutral-400">&ndash;</span>
            <input
              guaranteesControl
              class="min-w-0 flex-1 tabular-nums"
              name="endDate"
              type="date"
              [ngModel]="endDate()"
              (ngModelChange)="endDate.set($event)"
            />
          </span>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.drugComment" | translate }}</span>
          <textarea
            guaranteesControl
            class="w-full resize-y"
            name="comment"
            rows="2"
            [ngModel]="comment()"
            (ngModelChange)="comment.set($event)"
          ></textarea>
        </label>
      </section>
    }

    <!-- An amendment replaces what it states, so a form that never asked would clear these. -->
    <fieldset class="flex min-w-0 flex-col gap-3 border-t border-neutral-100 pt-3 sm:flex-row">
      <label class="flex min-w-0 flex-1 flex-col gap-1">
        <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.reserved" | translate }}</span>
        <span class="flex items-center gap-1.5">
          <!-- The spinner sits inside the box, where every searching control in this UI puts it. -->
          <span class="relative flex min-w-0 flex-1 items-center">
            <input
              guaranteesControl
              class="w-full tabular-nums"
              [class.pr-7]="reservedLoading()"
              name="reserved"
              type="number"
              min="0"
              step="0.01"
              [ngModel]="reserved()"
              (ngModelChange)="setReserved($event)"
            />
            @if (reservedLoading()) {
              <guarantees-spinner class="pointer-events-none absolute inset-y-0 right-2 text-neutral-400"></guarantees-spinner>
            }
          </span>
          <span class="shrink-0 text-sm text-neutral-400">&#8372;</span>
        </span>
      </label>

      <label class="flex min-w-0 flex-1 flex-col gap-1">
        <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.coverageFranchise" | translate }}</span>
        <span class="flex items-center gap-1.5">
          <span class="relative flex min-w-0 flex-1 items-center">
            <input
              guaranteesControl
              class="w-full tabular-nums"
              name="coverageFranchise"
              type="number"
              min="0"
              max="100"
              [ngModel]="coverageFranchise()"
              (ngModelChange)="setCoverageFranchise($event)"
            />
          </span>
          <span class="shrink-0 text-sm text-neutral-400">%</span>
        </span>
      </label>
    </fieldset>

    <!--
    A line may be filed under several. The dictionary runs to dozens, so they are ticked in a
    searchable list that stays open for the next one rather than laid out as a wall of chips, and
    what has been taken is gathered above it where any of it can be taken back off.
  -->
    <fieldset class="flex flex-col gap-1.5 border-t border-neutral-100 pt-3">
      <legend
        guaranteesLabel
        required
        class="pb-1"
        [missing]="categoriesMissing()"
      >
        {{ "guaranteeV2.label.categories" | translate }}
      </legend>

      <guarantees-category-picker
        [value]="categories()"
        (valueChange)="categories.set($event)"
      ></guarantees-category-picker>

      <span [class]="fieldMessage">
        @if (categoriesMissing()) {
          {{ "guaranteeV2.message.processCategoriesRequired" | translate }}
        }
      </span>
    </fieldset>

    <!--
      On a flexible package the line is drawn against one of the package options. Offered as soon as the
      line is categorised — asked against its categories and diagnosis as they stand, so picking a category
      re-reads which options fit — and owed once the line is ruled a grant.
    -->
    @if (offersPackageOption()) {
      <fieldset class="flex flex-col gap-1.5 border-t border-neutral-100 pt-3">
        <legend
          guaranteesLabel
          class="pb-1"
          [required]="asksPackageOption()"
          [missing]="packageOptionMissing()"
        >
          {{ "guaranteeV2.label.packageOption" | translate }}
        </legend>
        <p class="text-xs text-neutral-500">{{ "guaranteeV2.message.packageOptionHint" | translate }}</p>
        <guarantees-package-option-picker
          [userId]="memberUserId"
          [categories]="categories()"
          [diagnosisCode]="diagnosisCode()"
          [asOf]="data.guaranteeCreatedAt"
          [value]="packageSelection()"
          (valueChange)="setPackageSelection($event)"
          (touched)="touchPackageSelection()"
          (subOptionChosen)="fillFranchiseFrom($event)"
          (statusChange)="packageOptionsStatus.set($event)"
        ></guarantees-package-option-picker>

        <span [class]="fieldMessage">
          @if (packageOptionMissing()) {
            {{ "guaranteeV2.message.processOptionRequired" | translate }}
          }
        </span>
      </fieldset>
    }

    <!-- A written line has a history to answer to. A new one and an unsent draft have none. -->
    @if (isEdit) {
      <fieldset class="flex flex-col gap-1.5 border-t border-neutral-100 pt-3">
        <legend
          guaranteesLabel
          required
          class="pb-1"
          [missing]="editReasonMissing()"
        >
          {{ "guaranteeV2.label.editReason" | translate }}
        </legend>

        <guarantees-option-picker
          wrap
          maxHeight="max-h-56"
          [options]="editReasons"
          [placeholder]="'guaranteeV2.message.editReasonPlaceholder' | translate"
          [emptyMessage]="'guaranteeV2.message.reasonNoMatch' | translate"
          [value]="editReason()"
          (valueChange)="editReason.set($event)"
        ></guarantees-option-picker>

        <span [class]="fieldMessage">
          @if (editReasonMissing()) {
            {{ "guaranteeV2.message.editReasonRequired" | translate }}
          }
        </span>
      </fieldset>
    }

    <!-- Only the guarantee's own diagnoses: the backend refuses any other. -->
    @if (diagnoses.length > 0) {
      <fieldset class="flex flex-col gap-1.5 border-t border-neutral-100 pt-3">
        <legend class="pb-1 text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.diagnosis" | translate }}</legend>

        <div class="flex flex-wrap gap-1">
          @for (entry of diagnoses; track entry.id) {
            @let coding = codingOf(entry);

            <button
              guaranteesToggle
              [pressed]="diagnosisId() === entry.id"
              type="button"
              [matTooltip]="coding?.name ?? ''"
              (click)="setDiagnosis(entry.id)"
            >
              <span class="shrink-0 font-mono">{{ coding?.code }}</span>
              <span class="min-w-0 truncate font-normal">{{ coding?.name }}</span>
            </button>
          }
        </div>

        @if (!diagnosisId()) {
          <span class="text-sm text-neutral-400">{{ "guaranteeV2.message.decisionNoDiagnosis" | translate }}</span>
        }
      </fieldset>
    }

    <!--
    The ruling, in the same pass as the line it answers. A manager adding several lines answers each
    one in the dialog they are already in, rather than writing them all and then returning to rule.
    Left unanswered the line is raised undecided, which is where every line starts — so «без
    рішення» is one of the three and not the absence of a choice.
  -->
    @if (collectsDecision) {
      <fieldset class="flex flex-col gap-1.5 border-t border-neutral-100 pt-3">
        <legend
          guaranteesLabel
          class="pb-1"
        >
          {{ "guaranteeV2.label.decision" | translate }}
        </legend>

        <span class="flex flex-wrap items-center gap-1.5">
          <span [class]="segmentedGroup">
            @for (answer of answers; track answer) {
              <button
                guaranteesToggle
                size="sm"
                shape="segment"
                type="button"
                [pressed]="decision() === answer"
                [disabled]="answer === guaranteed && !!grantBlockerKey()"
                (click)="setDecision(answer)"
              >
                <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">{{ decisionIcon[answer] }}</mat-icon>
                {{ decisionLabelKey[answer] | translate }}
              </button>
            }
            <button
              guaranteesToggle
              size="sm"
              shape="segment"
              type="button"
              [pressed]="!decision()"
              (click)="setDecision()"
            >
              {{ "guaranteeV2.label.decisionPending" | translate }}
            </button>
          </span>

          <!-- Beside the control rather than on it: a disabled button cannot carry a tooltip. -->
          @if (grantBlockerKey(); as blocker) {
            <span class="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning-100 px-2 py-0.5 text-xs font-semibold text-warning-800">
              <mat-icon class="h-3! w-3! shrink-0 text-xs! leading-3!">warning</mat-icon>
              {{ blocker | translate }}
            </span>
          }
        </span>

        <!--
          Grounds belong to the answer given, so the control is closed until there is one — held in
          the layout rather than revealed by the choice, which would resize the dialog mid-answer.
        -->
        <guarantees-option-picker
          wrap
          maxHeight="max-h-56"
          [options]="decisionReasonOptions()"
          [disabled]="!decision()"
          [placeholder]="decisionReasonPlaceholderKey() | translate"
          [emptyMessage]="'guaranteeV2.message.reasonNoMatch' | translate"
          [value]="decisionReason()"
          (valueChange)="decisionReason.set($event)"
        ></guarantees-option-picker>

        <span [class]="fieldMessage">
          @if (decisionReasonMissing()) {
            {{ "guaranteeV2.message.decisionReasonRequired" | translate }}
          }
        </span>
      </fieldset>
    }

    @if (isMedication()) {
      <!-- Last of all, and folded: stating how a drug is taken is optional, and most lines never do. -->
      <section class="flex min-w-0 flex-col rounded-lg border border-neutral-200">
        <button
          class="flex items-center gap-1.5 px-2.5 py-2 text-sm font-semibold text-neutral-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
          type="button"
          (click)="scheduleExpanded.set(!scheduleExpanded())"
        >
          <mat-icon class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400">{{ scheduleExpanded() ? "expand_less" : "expand_more" }}</mat-icon>
          {{ "guaranteeV2.label.schedule" | translate }}
        </button>

        @if (scheduleExpanded()) {
          <div class="flex min-w-0 flex-col gap-3 border-t border-neutral-100 px-2.5 py-2">
            <div class="flex min-w-0 flex-col gap-3 sm:flex-row">
              <label class="flex min-w-0 flex-1 flex-col gap-1">
                <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.dose" | translate }}</span>
                <span class="flex items-center gap-1.5">
                  <input
                    guaranteesControl
                    class="min-w-0 flex-1 tabular-nums"
                    name="dose"
                    type="number"
                    min="0"
                    [ngModel]="dose()"
                    (ngModelChange)="dose.set($event)"
                  />
                  <input
                    guaranteesControl
                    class="w-20 shrink-0"
                    name="doseUnit"
                    type="text"
                    autocomplete="off"
                    [placeholder]="'guaranteeV2.message.doseUnitPlaceholder' | translate"
                    [ngModel]="doseUnit()"
                    (ngModelChange)="doseUnit.set($event)"
                  />
                </span>
              </label>

              <label class="flex min-w-0 flex-1 flex-col gap-1">
                <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.frequency" | translate }}</span>
                <span class="flex items-center gap-1.5">
                  <input
                    guaranteesControl
                    class="min-w-0 flex-1 tabular-nums"
                    name="frequency"
                    type="number"
                    min="0"
                    [ngModel]="frequency()"
                    (ngModelChange)="frequency.set($event)"
                  />
                  <span class="shrink-0 text-sm text-neutral-400">/</span>
                  <input
                    guaranteesControl
                    class="w-14 shrink-0 tabular-nums"
                    name="period"
                    type="number"
                    min="1"
                    [ngModel]="period()"
                    (ngModelChange)="period.set($event)"
                  />
                  <guarantees-option-picker
                    class="w-20 shrink-0"
                    [options]="timeUnitOptions"
                    [clearable]="false"
                    [value]="periodUnit()"
                    (valueChange)="setPeriodUnit($event)"
                  ></guarantees-option-picker>
                </span>
              </label>
            </div>

            <div class="flex min-w-0 flex-col gap-3 sm:flex-row">
              <label class="flex min-w-0 flex-1 flex-col gap-1">
                <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.duration" | translate }}</span>
                <span class="flex items-center gap-1.5">
                  <input
                    guaranteesControl
                    class="min-w-0 flex-1 tabular-nums"
                    name="duration"
                    type="number"
                    min="0"
                    [ngModel]="duration()"
                    (ngModelChange)="duration.set($event)"
                  />
                  <guarantees-option-picker
                    class="w-20 shrink-0"
                    [options]="timeUnitOptions"
                    [clearable]="false"
                    [value]="durationUnit()"
                    (valueChange)="setDurationUnit($event)"
                  ></guarantees-option-picker>
                </span>
              </label>

              <label class="flex min-w-0 flex-1 flex-col gap-1">
                <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.mealRelation" | translate }}</span>
                <guarantees-option-picker
                  [options]="mealRelationOptions"
                  [clearable]="false"
                  [value]="mealRelation()"
                  (valueChange)="setMealRelation($event)"
                ></guarantees-option-picker>
              </label>
            </div>

            <button
              guaranteesToggle
              [pressed]="asNeeded()"
              shape="card"
              layout="block"
              size="lg"
              type="button"
              (click)="asNeeded.set(!asNeeded())"
            >
              <guarantees-check-box [checked]="asNeeded()"></guarantees-check-box>
              <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.label.asNeeded" | translate }}</span>
            </button>

            <!-- What the schedule adds up to, shown where it is stated so a wrong figure is caught here. -->
            @if (dailyDose() !== null) {
              <p class="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-neutral-50 px-2 py-1.5 text-sm text-neutral-600">
                <span class="tabular-nums"
                  ><span class="text-neutral-400">{{ "guaranteeV2.label.dailyDose" | translate }}:</span> {{ dailyDose() }} {{ doseUnit() }}</span
                >
                @if (courseDose() !== null) {
                  <span class="tabular-nums"
                    ><span class="text-neutral-400">{{ "guaranteeV2.label.courseDose" | translate }}:</span> {{ courseDose() }} {{ doseUnit() }}</span
                  >
                }
              </p>
            }
          </div>
        }
      </section>
    }
  </div>

  <footer [class]="layout.footer">
    <button
      class="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium text-neutral-500 transition-colors duration-150 hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
      type="button"
      (click)="cancel()"
    >
      {{ "guaranteeV2.action.cancel" | translate }}
    </button>

    <button
      class="ml-auto inline-flex items-center gap-1 rounded-full bg-primary-600 px-3 py-1.5 text-sm font-semibold text-neutral-0 transition-colors duration-150 hover:bg-primary-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-40 motion-reduce:transition-none"
      type="button"
      [disabled]="!canSubmit()"
      [matTooltip]="submitBlockKey() | translate"
      (click)="submit()"
    >
      <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">{{ isCreate ? "add" : "save" }}</mat-icon>
      {{ submitLabelKey | translate }}
    </button>
  </footer>
</div>
